import Flex from '@/components/common/Flex';
import { useCheckBudgetQuery } from '@/app/common/api/accessLevelApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Card, Col, ProgressBar, Row } from 'react-bootstrap';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import SubtleBadge from '@/components/common/SubtleBadge';
import { accessLevelRenderer } from '@/app/Domain/components/DomainCard';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import SimpleBarReact from 'simplebar-react';
import { useGetDashboardNotifsQuery } from '@/app/Report/api/priceApi';
import HtmlParser from '@/app/common/components/CellRenderers/HtmlParser';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import timeStampToDate from '@/helpers/timeStampToDate';
import useDomain from '@/hooks/useDomain';
import HelpTooltip from '../common/components/HelpTooltip';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

type DashboardActivity = {
  id: string | number;
  created: string;
  badge?: string;
  title?: string;
  text?: string;
};

type NotifDashboardProps = {
  activity: DashboardActivity;
  isLast: boolean;
};

type NotifDashboardSkeletonProps = {
  isLast?: boolean;
};

const Dashboard = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const { domain_slug } = useDomain();

  const { data: budgetData, isFetching } = useCheckBudgetQuery(domain_slug);
  
  const { data: notifData } = useGetDashboardNotifsQuery();
  return (
    <>
      <ToolsBreadcrumb parentName="" parentUrl={`/`} childs={[]} />
      <Row className="my-3 g-3">
        {/* Budget */}
        <Col xxl={8}>
          <Card>
            <Card.Body>
              <div className="p-3">
                <div className="gap-5 d-flex flex-column flex-md-row">
                  <Flex direction="column" className="gap-1 mx-auto w-100">
                    <Flex className="position-relative" alignItems="center">
                      <div
                        style={{
                          fontSize: '1.70rem',
                          width: '3.125rem',
                          height: '3.425rem'
                        }}
                        className="icon-item  border rounded-3 shadow-none me-2"
                      >
                        {isFetching ? (
                          <TextLoading width={34} height={34} isWidthPx />
                        ) : (
                          <img
                            width="34"
                            className="rounded-lg"
                            src={`https://static.mizfa.tools/img/price-logo/lion-${budgetData?.data?.tariff_group === 0 ? 1 : budgetData?.data?.tariff_group}.svg`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <h6 className="mb-2 stretched-link fs-0 fw-black text-800">
                          {isFetching ? (
                            <TextLoading isWidthPx width={100} height={15} />
                          ) : budgetData?.data?.tariff_name ? (
                            budgetData?.data?.tariff_name
                          ) : (
                            'بدون طرح'
                          )}
                          &nbsp;
                          {budgetData?.data?.tariff_period && (
                            <span className="fs--2 fw-bold text-600">
                              ({budgetData?.data?.tariff_period})
                            </span>
                          )}
                        </h6>
                        <div className="mb-0 fs--1 fw-medium text-800">
                          {isFetching ? (
                            <TextLoading isWidthPx width={165} />
                          ) : (
                            <>
                              مالک:&nbsp;
                              {budgetData?.data?.owner}
                              {budgetData?.data?.access_level !== 1 && (
                                <div className="mt-1 d-flex align-items-center gap-1">
                                  دسترسی شما:&nbsp;
                                  <SubtleBadge
                                    pill
                                    bg={
                                      accessLevelRenderer(
                                        budgetData?.data?.access_level
                                      ).color
                                    }
                                    className="fs--2"
                                  >
                                    {
                                      accessLevelRenderer(
                                        budgetData?.data?.access_level
                                      ).label
                                    }
                                  </SubtleBadge>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </Flex>
                    {budgetData?.data?.access_level === 1 ? (
                      <Flex className="gap-2 mt-2 mb-3">
                        <Link
                          to="/prorogate-or-upgrade/"
                          className={classNames('text-decoration-none', {
                            'disabled-link': isFetching
                          })}
                        >
                          <Button
                            size="sm"
                            className="d-flex align-items-center gap-2"
                            variant="falcon-primary"
                          >
                            <FontAwesomeIcon icon="level-up" />
                            خرید طرح
                          </Button>
                        </Link>
                      </Flex>
                    ) : (
                      <div className="d-flex align-items-center fs--1 mt-2 mb-3">
                        <div
                          className={`bg-warning d-flex flex-center me-2 rounded-circle shadow-sm border-0`}
                          style={{ height: '1.2rem', width: '1.2rem' }}
                        >
                          <FontAwesomeIcon
                            icon={'exclamation-circle'}
                            className="text-white fs--1"
                          />
                        </div>
                        خرید طرح دامنه فقط توسط مالک انجام شود.
                      </div>
                    )}
                    <Flex direction="column" className="gap-3">
                      <Row
                        style={{ width: isMdUp ? '290px' : '100%' }}
                        className={classNames('g-0 align-items-center pb-3', {
                          // 'border-top pt-3': index !== 0
                        })}
                      >
                        <Col className="pe-md-4 pe-0">
                          <Flex
                            className="w-100"
                            alignItems="center"
                            justifyContent="between"
                          >
                            <h6 className="fs-0 d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon="calendar-alt" /> اشتراک
                              {(() => {
                                const total = budgetData?.data?.panel_duration;
                                const used =
                                  isFetching ||
                                  isNaN(
                                    total -
                                      budgetData?.data?.remaining_days_from_plan
                                  )
                                    ? 0
                                    : total -
                                      budgetData?.data
                                        ?.remaining_days_from_plan;

                                const percentage = total
                                  ? (used / total) * 100
                                  : 0;

                                if (percentage >= 100) {
                                  return (
                                    <span
                                      className="text-danger fs--2"
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      (پایان یافته)
                                    </span>
                                  );
                                }

                                return null;
                              })()}
                            </h6>
                            <h6 dir="ltr" className="fs--1 text-600">
                              {budgetData?.data?.panel_duration} /{' '}
                              {isFetching ||
                              isNaN(
                                budgetData?.data?.panel_duration -
                                  budgetData?.data?.remaining_days_from_plan
                              )
                                ? 0
                                : budgetData?.data?.panel_duration -
                                  budgetData?.data?.remaining_days_from_plan}
                            </h6>
                          </Flex>

                          {(() => {
                            const total = budgetData?.data?.panel_duration;
                            const used =
                              isFetching ||
                              isNaN(
                                total -
                                  budgetData?.data?.remaining_days_from_plan
                              )
                                ? 0
                                : total -
                                  budgetData?.data?.remaining_days_from_plan;

                            const percentage = total ? (used / total) * 100 : 0;

                            let variant = 'primary';
                            if (percentage >= 100) variant = 'danger';
                            else if (percentage >= 90) variant = 'warning';

                            return (
                              <ProgressBar
                                className="mt-xxl-auto"
                                style={{ height: 5 }}
                              >
                                <ProgressBar
                                  variant={variant}
                                  now={used}
                                  max={total}
                                  className="rounded-3"
                                />
                              </ProgressBar>
                            );
                          })()}
                        </Col>
                      </Row>
                      <Row
                        style={{ width: isMdUp ? '290px' : '100%' }}
                        className={classNames('g-0 align-items-center pb-3', {
                          // 'border-top pt-3': index !== 0
                        })}
                      >
                        <Col className="pe-md-4 pe-0">
                          <Flex
                            className="w-100"
                            alignItems="center"
                            justifyContent="between"
                          >
                            <h6 className="fs-0 d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon="globe" /> دامنه
                            </h6>
                            <h6 dir="ltr" className="fs--1 text-600">
                              {budgetData?.data?.domains?.total_budget} /{' '}
                              {budgetData?.data?.domains?.num_of_used}
                            </h6>
                          </Flex>

                          <ProgressBar
                            className="mt-xxl-auto"
                            style={{ height: 5 }}
                          >
                            <ProgressBar
                              variant="primary"
                              now={budgetData?.data?.domains?.num_of_used}
                              max={budgetData?.data?.domains?.total_budget}
                              className="rounded-3"
                            />
                          </ProgressBar>
                        </Col>
                      </Row>
                    </Flex>
                  </Flex>
                  <Flex
                    direction="column"
                    className="gap-1 mx-auto w-100 ps-lg-6 border-lg-start"
                  >
                    <Row
                      style={{ width: isMdUp ? '290px' : '100%' }}
                      className={classNames('g-0 align-items-center pb-3', {
                        // 'border-top pt-3': index !== 0
                      })}
                    >
                      <Col className="pe-md-4 pe-0">
                        <Flex
                          className="w-100"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <h6 className="fs--1 d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon="chart-line" /> ردیاب کلمات
                          </h6>
                          <h6 dir="ltr" className="fs--1 text-600">
                            {budgetData?.data?.keywords?.total_budget} /{' '}
                            {budgetData?.data?.keywords?.num_of_used}
                          </h6>
                        </Flex>

                        <ProgressBar
                          className="mt-xxl-auto"
                          style={{ height: 5 }}
                        >
                          <ProgressBar
                            variant="primary"
                            now={budgetData?.data?.keywords?.num_of_used}
                            max={budgetData?.data?.keywords?.total_budget}
                            className="rounded-3"
                          />
                        </ProgressBar>
                      </Col>
                    </Row>
                    <Row
                      style={{ width: isMdUp ? '290px' : '100%' }}
                      className={classNames('g-0 align-items-center pb-3', {
                        // 'border-top pt-3': index !== 0
                      })}
                    >
                      <Col className="pe-md-4 pe-0">
                        <Flex
                          className="w-100"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <h6 className="fs--1 d-flex align-items-center">
                            <FontAwesomeIcon className="me-2" icon="search" />{' '}
                            تحقیق کلمات (کیورد پلنر)
                            <HelpTooltip
                              placement="top"
                              html="این محدودیت ماهانه است."
                            />
                          </h6>
                          <h6 dir="ltr" className="fs--1 text-600">
                            {budgetData?.data?.keyword_planner?.total_budget} /{' '}
                            {budgetData?.data?.keyword_planner?.num_of_used}
                          </h6>
                        </Flex>

                        <ProgressBar
                          className="mt-xxl-auto"
                          style={{ height: 5 }}
                        >
                          <ProgressBar
                            variant="primary"
                            now={budgetData?.data?.keyword_planner?.num_of_used}
                            max={
                              budgetData?.data?.keyword_planner?.total_budget
                            }
                            className="rounded-3"
                          />
                        </ProgressBar>
                      </Col>
                    </Row>
                    <Row
                      style={{ width: isMdUp ? '290px' : '100%' }}
                      className={classNames('g-0 align-items-center pb-3', {
                        // 'border-top pt-3': index !== 0
                      })}
                    >
                      <Col className="pe-md-4 pe-0">
                        <Flex
                          className="w-100"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <h6 className="fs--1 d-flex align-items-center">
                            <FontAwesomeIcon icon="search" className="me-2" />{' '}
                            تحقیق کلمات (میزان جستجو)
                            <HelpTooltip
                              placement="top"
                              html="این محدودیت ماهانه است."
                            />
                          </h6>
                          <h6 dir="ltr" className="fs--1 text-600">
                            {budgetData?.data?.search_volume?.total_budget} /{' '}
                            {budgetData?.data?.search_volume?.num_of_used}
                          </h6>
                        </Flex>

                        <ProgressBar
                          className="mt-xxl-auto"
                          style={{ height: 5 }}
                        >
                          <ProgressBar
                            variant="primary"
                            now={budgetData?.data?.search_volume?.num_of_used}
                            max={budgetData?.data?.search_volume?.total_budget}
                            className="rounded-3"
                          />
                        </ProgressBar>
                      </Col>
                    </Row>
                    <Row
                      style={{ width: isMdUp ? '290px' : '100%' }}
                      className={classNames('g-0 align-items-center pb-3', {
                        // 'border-top pt-3': index !== 0
                      })}
                    >
                      <Col className="pe-md-4 pe-0">
                        <Flex
                          className="w-100"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <h6 className="fs--1 d-flex align-items-center">
                            <FontAwesomeIcon className="me-2" icon="trophy" />{' '}
                            ابزار SERP
                            <HelpTooltip
                              placement="top"
                              html="این محدودیت ماهانه است."
                            />
                          </h6>
                          <h6 dir="ltr" className="fs--1 text-600">
                            {budgetData?.data?.serp?.total_budget} /{' '}
                            {budgetData?.data?.serp?.num_of_used}
                          </h6>
                        </Flex>

                        <ProgressBar
                          className="mt-xxl-auto"
                          style={{ height: 5 }}
                        >
                          <ProgressBar
                            variant="primary"
                            now={budgetData?.data?.serp?.num_of_used}
                            max={budgetData?.data?.serp?.total_budget}
                            className="rounded-3"
                          />
                        </ProgressBar>
                      </Col>
                    </Row>
                    <Row
                      style={{ width: isMdUp ? '290px' : '100%' }}
                      className={classNames('g-0 align-items-center pb-3', {
                        // 'border-top pt-3': index !== 0
                      })}
                    >
                      <Col className="pe-md-4 pe-0">
                        <Flex
                          className="w-100"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <h6 className="fs--1 d-flex align-items-center">
                            <FontAwesomeIcon className="me-2" icon="link" />
                            بک لینک
                            {/* <HelpTooltip
                              placement="top"
                              html="این محدودیت ماهانه است."
                            /> */}
                          </h6>
                          {/* <h6 dir="ltr" className="fs--1 text-600">
                            ∞ /{' '}
                            {budgetData?.data?.backlink?.num_of_used}
                          </h6> */}
                        </Flex>

                        <ProgressBar
                          className="mt-xxl-auto"
                          style={{ height: 5 }}
                        >
                          <ProgressBar
                            variant="primary"
                            now={budgetData?.data?.backlink?.num_of_used}
                            max={budgetData?.data?.backlink?.total_budget}
                            className="rounded-3"
                          />
                        </ProgressBar>
                      </Col>
                    </Row>
                  </Flex>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xxl={4}>
          <Card className="h-100 recent-activity-card">
            <FalconCardHeader title="اطلاعیه" titleTag="h6" />
            <SimpleBarReact style={{ height: isMdUp ? '15.9rem' : '29.9rem' }}>
              <Card.Body className="ps-2 recent-activity-body-height">
                {!isFetching ? (
                  <>
                    {notifData?.items.map((activity, index) => (
                      <NotifDashboard
                        key={activity.id}
                        activity={activity}
                        isLast={index === notifData?.items.length - 1}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    {[0, 1, 2].map((activity, index) => (
                      <NotifDashboardSkeleton key={index} />
                    ))}
                  </>
                )}
              </Card.Body>
            </SimpleBarReact>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Dashboard;

const NotifDashboard = ({ activity, isLast }: NotifDashboardProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const variantToIcon = (variant?: string): IconProp => {
    switch (variant) {
      case 'danger':
        return 'times';
      case 'success':
        return 'check';
      case 'warning':
        return 'exclamation-circle';
      case 'info':
        return 'envelope';
      default:
        return 'envelope';
    }
  };
  const date = new Date(activity.created).getTime();
  const jalali = timeStampToDate(Math.floor(date / 1000))?.slice(8);
  return (
    <Row
      className={classNames('g-3', {
        'pb-x1': !isLast
      })}
    >
      <Col xs="auto" className="ps-2 ms-2">
        <div className="ps-2">
          <div className="icon-item icon-item-sm rounded-circle bg-200 shadow-none">
            <FontAwesomeIcon
              icon={variantToIcon(activity?.badge)}
              className={`text-${activity?.badge}`}
            />
          </div>
        </div>
      </Col>
      <Col>
        <Row className={classNames('g-3', { 'border-bottom pb-1': !isLast })}>
          <Col>
            <h6 className="text-800 fs-0 fs-md--1 mb-2">
              {activity?.title ? activity?.title : ''}
            </h6>
            <div className="fs--1 text-600 mb-0">
              {activity?.text ? <HtmlParser html={activity.text} /> : ''}
            </div>
          </Col>
          <Col xs={12} md="auto">
            <p className="fs--2 text-500 mb-3 mb-md-0">
              {jalali ? jalali?.toPersianDigit() : ''}
            </p>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
const NotifDashboardSkeleton = ({ isLast = false }: NotifDashboardSkeletonProps) => {
  return (
    <Row
      className={classNames('g-3', {
        'pb-x1': !isLast
      })}
    >
      <Col xs="auto" className="ps-2 ms-2">
        <div className="ps-2">
          <div className="icon-item icon-item-sm rounded-circle bg-200 shadow-none">
            <FontAwesomeIcon icon={'envelope'} className={`text-info`} />
          </div>
        </div>
      </Col>
      <Col>
        <Row className={classNames('g-3', { 'border-bottom pb-1': !isLast })}>
          <Col>
            <h6 className="text-800 mb-1">
              <TextLoading height={15} isWidthPx width={50} />
            </h6>
            <p className="fs--1 text-600 mb-0">
              <TextLoading height={40} isWidthPx width={200} />
            </p>
          </Col>
          <Col xs="auto">
            <p className="fs--2 text-500 mb-0">
              <TextLoading height={25} isWidthPx width={40} />
            </p>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
